import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { tap, catchError, switchMap } from 'rxjs/operators';
import { environment } from './../../../../environments/environment';
import { API } from '../../API';
import { FEDERATION_SLUG } from '../../../../main';

// Le serveur n'expose que des numéros PUBLICS : `club_id` est le `club_fed_seq`, jamais la PK
// réelle. La fédération n'a pas de fed_seq (c'est le locataire) — son identifiant public est
// l'acronyme ; sa PK n'est plus renvoyée. (Les anciennes clés brutes et colonnes héritées
// `federation_id`/`Cle_CLUB`/`Statut_CLUB`/`client_id` ne sont plus envoyées : rien ne les lisait.)
export interface Club {
  club_id: number;            // = club_fed_seq (public)
  club_code: string;
  club_name: string;
  club_status?: 'ACTI' | 'INAC' | 'DESA';
  logo_club?: string;
}

export interface Federation {
  acornyme: string;
  federation_name: string;
  logo_url?: string;
}

export interface AuthResponse {
  success: boolean;
  user: {
    id: number;
    username: string;
    role: string;
    user_object_type: string;
    user_object_id?: number;
    permissions?: Record<string, string[]>;
  };
  club?: Club;
  federation?: Federation;
}

export interface User {
  id: number;
  username: string;
  role: string;
  role_code?: string;
  user_object_type: string;
  user_object_id?: number;
  permissions?: Record<string, string[]>;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly baseUrl = environment.apiUrl;
  private userDataSignal = signal<User | null>(null);
  private clubDataSignal = signal<Club | null>(null);
  private federationDataSignal = signal<Federation | null>(null);
  private slugLogoUrlSignal = signal<string | null>(null);
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(private httpClient: HttpClient) {}

  get federationSlug(): string | null {
    return FEDERATION_SLUG;
  }

  private clearOtherPlatformCache(platform: 'club' | 'fed'): void {
    if (platform === 'fed') {
      this.clubDataSignal.set(null);
    } else {
      this.federationDataSignal.set(null);
    }
  }

  /**
   * `username` maps to tab_user.user_login — there is no email column in the schema.
   * Same wire contract and chaining as sportiq-licence: after the POST succeeds the
   * cookie is set, so we read the full session (incl. permissions) from /auth/me.
   * If /auth/me fails we fall back to the login response so the user is still
   * signed in — otherwise a transient /me error would leave the app authenticated
   * server-side but logged out client-side, and the redirect would bounce back.
   */
  login(username: string, password: string, user_object_type: 'FEDE' | 'CLUB' = 'CLUB'): Observable<AuthResponse> {
    const platform: 'fed' | 'club' = user_object_type === 'FEDE' ? 'fed' : 'club';

    return this.httpClient.post<AuthResponse>(`${this.baseUrl}${API.AUTH.LOGIN}`, {
      username,
      password,
      federation_slug: this.federationSlug,
      user_object_type
    }, { withCredentials: true }).pipe(
      switchMap(loginResponse => {
        if (!loginResponse.success) return of(loginResponse);

        return this.httpClient.get<AuthResponse>(`${this.baseUrl}${API.AUTH.ME}`, { withCredentials: true }).pipe(
          tap(meResponse => {
            if (meResponse.success) this.applySession(meResponse, platform);
          }),
          catchError(() => {
            this.applySession(loginResponse, platform);
            return of(loginResponse);
          })
        );
      })
    );
  }

  private applySession(response: AuthResponse, platform: 'fed' | 'club'): void {
    this.clearOtherPlatformCache(platform);
    this.setUser(response.user);
    if (response.club) this.setClub(response.club);
    if (response.federation) this.setFederation(response.federation);
    this.currentUserSubject.next(response.user);
  }

  logout(): Observable<any> {
    return this.httpClient.post(`${this.baseUrl}${API.AUTH.LOGOUT}`, {}, { withCredentials: true }).pipe(
      tap(() => this.clearAuth()),
      catchError((error: any) => {
        this.clearAuth();
        return new Observable(observer => observer.complete());
      })
    );
  }

  refreshToken(): Observable<any> {
    return this.httpClient.post<AuthResponse>(`${this.baseUrl}${API.AUTH.REFRESH}`, {}, { withCredentials: true }).pipe(
      tap((response: any) => {
        if (response?.success && response.user) {
          this.setUser(response.user);
          if (response.club)       this.setClub(response.club);
          if (response.federation) this.setFederation(response.federation);
          this.currentUserSubject.next(response.user);
        }
      })
    );
  }

  /** Read the current session from the HttpOnly cookie (permissions recomputed server-side). */
  me(): Observable<AuthResponse> {
    return this.httpClient.get<AuthResponse>(`${this.baseUrl}${API.AUTH.ME}`, { withCredentials: true }).pipe(
      tap(response => {
        if (response?.success && response.user) {
          this.setUser(response.user);
          if (response.club)       this.setClub(response.club);
          if (response.federation) this.setFederation(response.federation);
          this.currentUserSubject.next(response.user);
        }
      })
    );
  }

  /**
   * Called by APP_INITIALIZER on every page load. Tries /auth/me first (cheap read,
   * no token rotation); if the access cookie has expired, falls back to /auth/refresh
   * to rotate it, then reads the session again.
   */
  tryRestoreSession(): Promise<void> {
    return new Promise(resolve => {
      this.me().subscribe({
        next: () => resolve(),
        error: () => {
          this.refreshToken().subscribe({
            next: () => this.me().subscribe({ next: () => resolve(), error: () => resolve() }),
            error: () => resolve(),
          });
        },
      });
    });
  }

  clearLocalAuth(): void {
    this.userDataSignal.set(null);
    this.clubDataSignal.set(null);
    this.federationDataSignal.set(null);
    this.currentUserSubject.next(null);
  }

  private clearAuth(): void {
    this.clearLocalAuth();
  }

  /**
   * Fin de session — vide l'authentification PUIS recharge la page.
   *
   * Le rechargement n'est pas une facilité, c'est la seule garantie. Une vingtaine de
   * services et de magasins sont `providedIn: 'root'`, donc des singletons qui survivent
   * à une navigation Angular : références du club, comptes bancaires, taux de TVA, circuit
   * de décaissement, fournisseurs. `clearLocalAuth()` ne remet à zéro que l'utilisateur.
   *
   * Sans rechargement, se reconnecter avec un AUTRE compte dans le même onglet fait lire
   * au nouveau les listes chargées pour le précédent — et si les deux comptes ne sont pas
   * de la même fédération, ce sont les saisons, catégories et types de frais d'une autre
   * fédération qui s'affichent.
   *
   * Vider chaque magasin un par un supposerait que personne n'en ajoute un sans penser à
   * l'inscrire ici. Le rechargement les couvre tous, y compris ceux qui n'existent pas
   * encore.
   */
  terminerSession(destination = '/accueil'): void {
    this.clearLocalAuth();
    window.location.href = destination;
  }

  setUser(user: User): void { this.userDataSignal.set(user); }
  getUser(): User | null { return this.userDataSignal(); }

  isAuthenticated(): boolean { return this.currentUserSubject.getValue() !== null; }
  isClub(): boolean { return this.getUser()?.user_object_type === 'CLUB'; }
  isFederation(): boolean { return this.getUser()?.user_object_type === 'FEDE'; }

  getHomeRoute(): string {
    if (this.isFederation()) return '/federation/dashboard';
    if (this.isClub()) return '/club/dashboard';
    return '/accueil';
  }

  setClub(club: Club): void { this.clubDataSignal.set(club); }

  setFederation(federation: Federation): void { this.federationDataSignal.set(federation); }
  getFederation(): Federation | null { return this.federationDataSignal(); }

  /** Logo de la fédération du slug, renvoyé par /api/federations/resolve (avant connexion). */
  setSlugLogoUrl(url: string | null): void { this.slugLogoUrlSignal.set(url); }
  readonly slugLogoUrl = this.slugLogoUrlSignal.asReadonly();
}
